import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { cn } from "../utls/utils";

export interface ThemedSwitchProps {
  name: string;
  label: string;
  description?: string;
  labelPosition?: "top" | "left";
  size?: "sm" | "md" | "lg";
  showSeparator?: boolean;
  activeColor?: string;
  inactiveColor?: string;
  thumbColor?: string;
  labelOnValue?: string;
  labelOffValue?: string;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  value: boolean;
}

const sizeClasses = {
  sm: "w-8 h-4 after:w-3 after:h-3",
  md: "w-11 h-6 after:w-5 after:h-5",
  lg: "w-14 h-7 after:w-6 after:h-6",
};

export function ThemedSwitch({
  name,
  label,
  description,
  labelPosition = "left",
  size = "md",
  showSeparator = true,
  activeColor = "#DEA895",
  inactiveColor = "#FDF9F7",
  thumbColor = "#C5705D",
  labelOnValue = "Yes",
  labelOffValue = "No",
  disabled = false,
  onChange,
  value,
}: ThemedSwitchProps) {
  return (
    <div className="my-3 space-y-1">
      {labelPosition === "top" && (
        <label
          htmlFor={name}
          className="text-terracotta-700 text-sm font-medium"
        >
          {label}
        </label>
      )}
      <div className="flex items-center justify-between">
        {labelPosition === "left" && (
          <div>
            <label
              htmlFor={name}
              className="text-terracotta-700 text-sm font-medium"
            >
              {label}
            </label>
            {description && (
              <p className="text-terracotta-600 text-xs">{description}</p>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-terracotta-600 text-base">
            {value ? labelOnValue : labelOffValue}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={value}
            disabled={disabled}
            onClick={() => !disabled && onChange(!value)}
            className={cn(
              "relative inline-flex flex-shrink-0 rounded-full transition-colors focus:outline-none border border-terracotta-200",
              sizeClasses[size],
              disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
            )}
            style={{ backgroundColor: value ? activeColor : inactiveColor }}
          >
            <span className="sr-only">{label}</span>
            <span
              className={cn(
                "absolute left-0.5 top-1/2 -translate-y-1/2 rounded-full bg-terracotta-100 shadow transform transition-transform border border-terracotta-200 w-5 h-5",
                value ? "translate-x-full" : "translate-x-0"
              )}
              style={{ backgroundColor: value ? thumbColor : "$cream100" }}
            />
          </button>
        </div>
      </div>
      {showSeparator && <div className="border-t border-terracotta-100 mt-1" />}
    </div>
  );
}

export function ThemedSwitchWithControl<T extends FieldValues>({
  name,
  control,
  ...props
}: Omit<ThemedSwitchProps, "onChange" | "value" | "name"> & {
  name: Path<T>;
  control: Control<T>;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <ThemedSwitch
          {...props}
          name={String(name)}
          onChange={onChange}
          value={value as boolean}
        />
      )}
    />
  );
}
